import React from 'react';

import { Spin as AntdSpin } from 'antd';

import {
  UpdateViolationDtoFormFieldEnum,
  UserDto,
  UserDtoProfileEnum,
} from 'lib_api/lib/api/gen';

import { Select } from 'components/WrappedComponents';
import { Empty } from 'components/WrappedComponents/Empty';
import { WaitingReferentielLoadingField } from 'components/WrappedComponents/Form/FormFields/Select/Referentiels/WaitingReferentielLoadingField';
import { FieldProps } from 'components/WrappedComponents/Form/FormWrapper/type';
import { useApi } from 'hooks/ApiStoreContext';
import { useRefAutoriteFourriere } from 'hooks/referentiels/useRefAutoriteFourriere';
import { useRefFourriere } from 'hooks/referentiels/useRefFourriere';
import { useRefUniteFoSearch } from 'hooks/referentiels/useRefUniteFoSearch';
import { FormPlaceholders } from 'types/enums/FormPlaceholders';
import { CSSUtils } from 'utils/CSSUtils';

import { ModificationUtilisateurFormValues } from '../ModalModification';
import {
  autoriteFourriereToItem,
  fourriereToItem,
  generateUpdateUserRequestDtoFromFormData,
  uniteFoToItem,
  validateUpdateUserRequest,
} from '../utils';

import '../../style.less';

interface SelectUniteRattachementProps
  extends FieldProps<ModificationUtilisateurFormValues> {
  userToUpdate: UserDto;
  disabled?: boolean;
}

function SelectUniteRattachement({
  values,
  userToUpdate,
  disabled = false,
}: SelectUniteRattachementProps): React.ReactElement {
  const cuController = useApi().ComptesUtilisateursControllerApi;
  const [refUniteFo, searchUniteFo, isFetchingUniteFo, fetchUniteFo] =
    useRefUniteFoSearch();
  const refAutoriteFourriere = useRefAutoriteFourriere();
  const refFourriere = useRefFourriere();

  const key: keyof ModificationUtilisateurFormValues = 'uniteRattachement';

  // content if no data found
  let notFoundContent = undefined;
  // default value in select
  let defaultValue: string | null = null;
  // list of options of select
  let selectItems = [];
  // action on search in select
  let onSearch = undefined;

  switch (userToUpdate.profile) {
    case UserDtoProfileEnum.GARDIEN_FOURRIERE:
      selectItems =
        refFourriere?.referenceDtoList
          .sort((a, b) => a.raisonSociale.localeCompare(b.raisonSociale))
          .map(fourriereToItem) ?? [];
      defaultValue = userToUpdate.idCorrelationFourriere;
      break;
    case UserDtoProfileEnum.AUTORITE_FOURRIERE:
      selectItems =
        refAutoriteFourriere?.referenceDtoList
          .sort((a, b) => a.libelleComplet.localeCompare(b.libelleComplet))
          .map(autoriteFourriereToItem) ?? [];
      defaultValue = userToUpdate.idCorrelationAutoriteFourriere;
      break;
    case UserDtoProfileEnum.FORCE_DE_L_ORDRE:
      selectItems =
        refUniteFo?.referenceDtoList
          .sort((a, b) => a.abreviation.localeCompare(b.abreviation))
          .map(uniteFoToItem) ??
        (userToUpdate.idCorrelationUniteFO !== undefined
          ? [
              {
                value: userToUpdate.idCorrelationUniteFO ?? '',
                displayValue: userToUpdate.libelleEntiteRattachement ?? '',
              },
            ]
          : []);
      notFoundContent = isFetchingUniteFo ? (
        <AntdSpin size="small" />
      ) : searchUniteFo !== undefined && searchUniteFo !== '' ? (
        <Empty />
      ) : null;
      defaultValue = userToUpdate.idCorrelationUniteFO;
      onSearch = fetchUniteFo;
      break;
    default:
      return <></>;
  }
  return refFourriere !== undefined && refAutoriteFourriere !== undefined ? (
    <Select
      wrapperProps={{
        values: values,
        fieldKey: key,
        itemProps: {
          label: 'Entité de rattachement',
          rules: [
            {
              required: true,
              validator: async (
                _rule,
                value: string | undefined,
              ): Promise<void> => {
                const requestObject = generateUpdateUserRequestDtoFromFormData({
                  ...values,
                  [key]: value,
                });
                const userId = userToUpdate.id;
                if (userId === undefined) {
                  throw new Error('user id should be defined');
                }

                return await validateUpdateUserRequest(
                  userId,
                  cuController,
                  requestObject,
                  UpdateViolationDtoFormFieldEnum.UNITE_RATTACHEMENT,
                );
              },
            },
          ],
          labelCol: { span: CSSUtils.defaultLabelCol },
          wrapperCol: { span: CSSUtils.largeWrapperCol },
          getValueProps: (value: unknown) => {
            if (typeof value === 'string') {
              return { value: value };
            }
            return {
              value: defaultValue,
            };
          },
        },
      }}
      optionProps={selectItems}
      selectProps={{
        disabled: disabled,
        placeholder: FormPlaceholders.Select,
        dropdownMatchSelectWidth: 350,
        suffixIcon: null,
        onSearch: onSearch,
        notFoundContent: notFoundContent,
      }}
    />
  ) : (
    <WaitingReferentielLoadingField title={'Entité de rattachement'} />
  );
}

export default SelectUniteRattachement;
